import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import type { ReportReason, ReportTarget } from './ReportSheet';
import { useAuth } from '@/lib/auth/auth';
import { APIError } from '@/lib/api/api';
import { showGuestWritePrompt } from '@/features/market/guestWrite';
import { handleReadOnlyMarketError } from '@/features/market/readOnlyMarket';
import { useWritableMarketGuard } from '@/features/market/useWritableMarketGuard';
import { trackReportSubmitted } from '@/lib/analytics/posthog';
import * as React from 'react';
import { Alert, Platform } from 'react-native';
import { useShallow } from 'zustand/react/shallow';

interface ReportRequest {
  event_id?: string;
  user_id?: string;
  comment_id?: string;
  reason: ReportReason;
  details?: string;
}

export function useReport() {
  const { session, request } = useAuth(
    useShallow((s) => ({ session: s.session, request: s.request }))
  );
  const ensureWritableMarket = useWritableMarketGuard();
  const bottomSheetRef = React.useRef<BottomSheetModal>(null);
  const [target, setTarget] = React.useState<ReportTarget | null>(null);

  const openReportSheet = React.useCallback(
    (reportTarget: ReportTarget) => {
      if (!session) {
        showGuestWritePrompt('report');
        return;
      }
      setTarget(reportTarget);
      bottomSheetRef.current?.present();
    },
    [session]
  );

  const closeReportSheet = React.useCallback(() => {
    if (Platform.OS !== 'web') {
      bottomSheetRef.current?.dismiss();
    } else {
      setTarget(null);
    }
  }, []);

  const handleDismiss = React.useCallback(() => {
    setTarget(null);
  }, []);

  const submitReport = React.useCallback(
    async (reason: ReportReason, details?: string) => {
      if (!target || !session) {
        return;
      }

      const body: ReportRequest = { reason };
      if (target.type === 'event') {
        body.event_id = target.id;
      } else if (target.type === 'user') {
        body.user_id = target.id;
      } else {
        body.comment_id = target.id;
      }
      if (details) {
        body.details = details;
      }

      try {
        if (!(await ensureWritableMarket())) {
          return;
        }

        await request('/reports', {
          method: 'POST',
          body: JSON.stringify(body),
        });

        trackReportSubmitted({
          reason,
          hasDetails: Boolean(details?.trim()),
          targetType: target.type,
        });

        closeReportSheet();

        // Show success feedback
        if (Platform.OS === 'web') {
          window.alert("Thanks for the report. We'll review it soon.");
        } else {
          Alert.alert(
            'Report submitted',
            "Thanks for the report. We'll review it soon.",
            [{ text: 'OK' }]
          );
        }
      } catch (error) {
        closeReportSheet();

        if (handleReadOnlyMarketError(error)) {
          return;
        }

        const message =
          error instanceof APIError && error.status === 429
            ? "You've submitted too many reports recently. Try again tomorrow."
            : 'Something went wrong. Please try again.';

        if (Platform.OS === 'web') {
          window.alert(message);
        } else {
          Alert.alert('Error', message, [{ text: 'OK' }]);
        }
      }
    },
    [closeReportSheet, ensureWritableMarket, request, session, target]
  );

  return {
    bottomSheetRef,
    target,
    openReportSheet,
    closeReportSheet,
    handleDismiss,
    submitReport,
  };
}
